<template>
  <div class="field-new">
    <label for="order-contact">{{ $t('createOrdersPopup.contacts_title') }}</label>
    <select id="order-contact" data-cy="order-contact" v-model="localSelectedContactId" class="with-arrow">
      <option disabled :value="undefined">{{ $t('global.select_placeholder') }}</option>
      <option v-for="contact in userContacts" :key="contact.id" :value="contact.id">
        {{ `${contact.firstName} ${contact.lastName}${hasBuyerFunction(contact) ? ' (Buyer)' : ''}` }}
      </option>
    </select>
    <div v-if="selectedContact" class="contact-info">
      <div>
        <span class="title">{{ $t('createOrdersPopup.contacts_roles') }}</span>
        <span class="description">{{ contactFunctions(selectedContact) }}</span>
      </div>
      <div>
        <span class="title">{{ $t('createOrdersPopup.contacts_email') }}</span>
        <span class="description">
          <a :href="`mailto:${selectedContact.email}`">{{ selectedContact.email }} </a>
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { SFContactModel } from '@/models/sf-contact.model';
import { useUserStore } from '@/stores/user';
import type { ContactModel } from '@onrunning/backstage-models';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const { hasBuyerFunction } = useUserStore();

const props = defineProps<{
  userContacts: ContactModel[];
  selectedContactId: string | undefined;
}>();

const emit = defineEmits<{
  'update:selectedContactId': [value: string | undefined];
}>();

const localSelectedContactId = computed({
  get: () => props.selectedContactId,
  set: (value) => emit('update:selectedContactId', value),
});

const selectedContact = computed(() => {
  return props.userContacts.find((contact) => contact.id === localSelectedContactId.value);
});

const contactFunctions = (contact: SFContactModel): string => {
  if (contact.userFunction) {
    return contact.userFunction.join(', ');
  }

  return t('createOrdersPopup.contact_has_no_roles').toString();
};
</script>

<style lang="scss" scoped>
.field-new {
  position: relative;

  i {
    position: absolute;
    right: 30px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
  }

  .contact-info {
    padding: 5px 10px;
    margin-top: 5px;
    background: var(--read-only-field-bg-color);
    color: var(--primary-text-bold-color);

    div {
      margin-bottom: 2px;
      display: flex;
      span.title {
        width: 50px;
      }
      span.description {
        font-weight: 600;
      }
    }
  }
}
</style>
